'use client'

/**
 * Modal shown by `useInactivityTimeout` when the warning threshold
 * fires. Renders a 5-minute countdown and two buttons.
 *
 * Mounted as a sibling at the top of `(app)/layout.tsx` for any
 * authenticated user so the warning works on every page.
 */

import { useEffect } from 'react'
import {
  useInactivityTimeout,
  INACTIVITY_WARNING_MS,
  INACTIVITY_LOGOUT_MS,
} from '@/lib/auth/use-inactivity-timeout'

interface Props {
  /**
   * Whether to mount the timer at all. Pass `userId !== null` from
   * the server-rendered layout - anonymous visitors don't need a
   * session-hygiene timer.
   */
  enabled: boolean
  warningAfterMs?: number
  logoutAfterMs?: number
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function InactivityWarningModal({
  enabled,
  warningAfterMs = INACTIVITY_WARNING_MS,
  logoutAfterMs = INACTIVITY_LOGOUT_MS,
}: Props) {
  const { showWarning, remainingMs, stayActive, signOutNow } =
    useInactivityTimeout({ enabled, warningAfterMs, logoutAfterMs })

  // Esc key dismisses (treats it as "stay signed in") while the modal
  // is open. We don't auto-dismiss on background clicks - the user
  // should make an explicit choice.
  useEffect(() => {
    if (!showWarning) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') stayActive()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [showWarning, stayActive])

  if (!enabled || !showWarning) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-warning-title"
      aria-describedby="inactivity-warning-body"
    >
      <div
        className="w-full max-w-md rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 shadow-md"
        style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
      >
        <h2
          id="inactivity-warning-title"
          className="text-lg font-semibold text-[var(--color-text-primary)]"
        >
          Are you still there?
        </h2>
        <p
          id="inactivity-warning-body"
          className="mt-2 text-sm text-[var(--color-text-secondary)]"
        >
          You&rsquo;ll be signed out in{' '}
          <span className="font-medium text-[var(--color-text-primary)]">
            {formatRemaining(remainingMs)}
          </span>{' '}
          due to inactivity. Stay signed in?
        </p>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={signOutNow}
            className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-4 py-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
          >
            Sign out now
          </button>
          <button
            type="button"
            onClick={stayActive}
            autoFocus
            className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:brightness-110"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  )
}
